/**
 * TanStack Query hooks — Submissions.
 *
 * Routes API consommées (voir `apps/api/src/routes/submissions.ts`) :
 *   GET    /v1/submissions?cursor&limit&project_id&status&insurer_code  listing paginé
 *   POST   /v1/submissions                                              création
 *   GET    /v1/submissions/:id                                          détail enrichi
 *                                                                      (anomalies + dernière
 *                                                                       extraction + files attachés)
 *   GET    /v1/submissions/:id/fields                                   extracted_fields courants
 *   PATCH  /v1/submissions/:id                                          update
 *   DELETE /v1/submissions/:id                                          soft-delete
 *
 * Note 9.A — `CreateSubmissionInput` exige `{ project_id, insurer_code,
 * insurer_name, detection_confidence? }`. Pas de `file_id` ni `status` au
 * moment de la création : la submission naît avec le statut DB par défaut
 * (`pending`), et le PDF est attaché via `app.files.submission_id` lors du
 * complete-upload (cf. `lib/queries/files.ts`).
 *
 * Détail enrichi : la forme exacte renvoyée par GET /v1/submissions/:id
 * inclut les anomalies, la dernière extraction et les files attachés. On
 * type ce détail localement (pas de schéma shared dédié — décision shared
 * "n'exporter que les schémas consommés ≥2 fois", cf. D23).
 */

import type { DbPage, InsurerCode, SubmissionStatus } from "@kollectivia/shared";
import {
  type CreateSubmissionInput,
  type SubmissionFilter,
  type UpdateSubmissionInput,
} from "@kollectivia/shared/schemas/submissions.js";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * Lecture API — colonnes exposées par `SUBMISSION_COLS` dans
 * `apps/api/src/routes/submissions.ts`. Voir caveat D24 (schéma local plutôt
 * que shared, à promouvoir au moment où MCP exposera ces lignes).
 *
 * Chunk 9.L.8 D42 wire-side consumption : `quote_request_id` +
 * `received_via_intake_event_id` exposés API depuis 9.L.7 (D42 ADR 0023 §2.3
 * finition). Nullable car submissions reçues spontanément hors quote_request
 * (cas legacy / saisie directe broker pré-9.G.1). Le UI 9.L.8 consomme le FK
 * pour afficher un badge « Réponse à QR <title> » sur les rows linked.
 */
export type SubmissionRow = {
  id: string;
  tenant_id: string;
  project_id: string;
  insurer_code: InsurerCode;
  insurer_name: string;
  status: SubmissionStatus;
  detection_confidence: number | null;
  notes: string | null;
  flags: Record<string, unknown> | null;
  created_at: string; // ISO timestamp
  updated_at: string;
  /** Chunk 9.L.8 D42 wire-side — FK vers app.quote_requests(id), NULL si soumission spontanée. */
  quote_request_id: string | null;
  /** Chunk 9.L.8 D42 wire-side — FK vers app.intake_events(id), NULL si saisie directe. */
  received_via_intake_event_id: string | null;
  /**
   * Chunk 9.AO.1 — le créneau assureur auquel cette proposition répond. NULL pour
   * un dépôt direct. C'est ce lien, et non `(project_id, insurer_code)`, qui dit
   * à quelle demande une réponse appartient.
   */
  responds_to_recipient_id: string | null;
};

/** Anomalies attachées (forme stable côté API). */
export type SubmissionAnomaly = {
  id: string;
  severity: "info" | "warn" | "high" | "critical";
  code: string;
  message: string;
  field_key: string | null;
  created_at: string;
};

/** Vue résumée d'un fichier attaché (sous-ensemble de `FILE_PUBLIC_COLS`). */
export type SubmissionAttachedFile = {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  kind: string;
  status: string;
  page_count: number | null;
  uploaded_at: string;
};

/**
 * Résumé d'une extraction tel que renvoyé par GET /v1/submissions/:id
 * sous la clé `last_extraction` (cf. apps/api/src/routes/submissions.ts,
 * `EXTRACTION_COLS`).
 *
 * Note 9.B : on aligne sur le nom **réel** côté API (`last_extraction`).
 * `completed_at` est le nom de colonne SQL (pas `finished_at`). On ne
 * dépouille pas la liste complète ici — ce résumé sert juste à amorcer
 * le polling au mount.
 */
export type SubmissionLastExtraction = {
  id: string;
  status: string;
  pass_id: string | null;
  model_id: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  /** 9.W.2 — dépréciée : ne plus afficher (pouvait porter un message fournisseur). */
  error_message: string | null;
  /** 9.W.2 — code d'échec de type fermé, seule surface affichable. */
  error_code: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
};

/**
 * Détail enrichi renvoyé par GET /v1/submissions/:id.
 *  - `last_extraction` : dernière extraction (1 row max), null si aucune.
 *  - `extractions`     : timeline complète (toutes passes confondues).
 *  - `files`           : fichiers attachés (kind/status/page_count/uploaded_at).
 *  - `anomalies`       : anomalies (résolues + non), triées par sévérité.
 */
export type SubmissionDetail = SubmissionRow & {
  anomalies: SubmissionAnomaly[];
  files: SubmissionAttachedFile[];
  last_extraction: SubmissionLastExtraction | null;
  extractions: SubmissionLastExtraction[];
};

export type SubmissionListResult = DbPage<SubmissionRow>;

export type SubmissionListParams = {
  cursor?: string | null;
  limit?: number;
} & Partial<SubmissionFilter>;

// -----------------------------------------------------------------------------
// Query keys
// -----------------------------------------------------------------------------

export const submissionsKeys = {
  all: ["submissions"] as const,
  list: (params: SubmissionListParams) =>
    ["submissions", "list", params] as const satisfies QueryKey,
  byProject: (projectId: string) =>
    ["submissions", "list", { project_id: projectId }] as const satisfies QueryKey,
  detail: (id: string) => ["submissions", "detail", id] as const satisfies QueryKey,
  fields: (id: string) => ["submissions", "fields", id] as const satisfies QueryKey,
};

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

function buildListPath(params: SubmissionListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.project_id) sp.set("project_id", params.project_id);
  if (params.status) sp.set("status", params.status);
  if (params.insurer_code) sp.set("insurer_code", params.insurer_code);
  const qs = sp.toString();
  return qs ? `/v1/submissions?${qs}` : "/v1/submissions";
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export function useSubmissions(params: SubmissionListParams) {
  return useQuery({
    queryKey: submissionsKeys.list(params),
    queryFn: () => api.get<SubmissionListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

/**
 * Sucre syntaxique courant — listing filtré par projet uniquement. Utilisé
 * par la section "Soumissions" de la page projet.
 */
export function useSubmissionsByProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId
      ? submissionsKeys.byProject(projectId)
      : submissionsKeys.byProject("__none__"),
    queryFn: () =>
      api.get<SubmissionListResult>(buildListPath({ project_id: projectId as string })),
    enabled: !!projectId,
  });
}

export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: id ? submissionsKeys.detail(id) : submissionsKeys.detail("__none__"),
    queryFn: () => api.get<SubmissionDetail>(`/v1/submissions/${id}`),
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------

export function useCreateSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubmissionInput) =>
      api.post<SubmissionRow>("/v1/submissions", input as unknown as Record<string, unknown>),
    onSuccess: (sub) => {
      void qc.invalidateQueries({ queryKey: submissionsKeys.all });
      void qc.invalidateQueries({
        queryKey: submissionsKeys.byProject(sub.project_id),
      });
      // La création d'une submission incrémente `submissions_count` sur le
      // projet — invalide aussi le détail projet pour rafraîchir le compteur.
      void qc.invalidateQueries({
        queryKey: ["projects", "detail", sub.project_id],
      });
    },
  });
}

/**
 * Rattacher, corriger ou détacher le créneau assureur d'une proposition — 9.AP.1.
 *
 * `recipientId: null` détache. Le geste change **trois** lectures d'un coup : la
 * proposition, la boucle assureur du dossier et ses échéances — un créneau
 * détaché redevient relançable. Les invalider toutes évite qu'un écran continue
 * d'annoncer une réponse qu'on vient de retirer.
 */
export function useUpdateRecipientLink(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { submissionId: string; recipientId: string | null }) =>
      api.patch<SubmissionRow>(`/v1/submissions/${args.submissionId}/recipient-link`, {
        recipient_id: args.recipientId,
      }),
    onSuccess: (sub) => {
      void qc.invalidateQueries({ queryKey: submissionsKeys.all });
      void qc.invalidateQueries({
        queryKey: ["projects", projectId ?? sub.project_id, "insurer-loop"],
      });
      void qc.invalidateQueries({
        queryKey: ["projects", projectId ?? sub.project_id, "deadlines"],
      });
      void qc.invalidateQueries({ queryKey: ["quote-requests"] });
    },
  });
}

export function useUpdateSubmission(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSubmissionInput) =>
      api.patch<SubmissionRow>(
        `/v1/submissions/${id}`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (sub) => {
      void qc.invalidateQueries({ queryKey: submissionsKeys.detail(id) });
      void qc.invalidateQueries({
        queryKey: submissionsKeys.byProject(sub.project_id),
      });
    },
  });
}

export function useDeleteSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/submissions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: submissionsKeys.all });
    },
  });
}
